import { useState, useCallback, useMemo, useRef } from "react";

/**
 * Overlay State Machine with Success Closure Law
 * 
 * States:
 * - idle: Initial state, form is empty or pristine
 * - editing: User has made changes (isDirty = true)
 * - processing: Form is being submitted (blocked from closing)
 * - submitted: Form was successfully submitted (can close immediately, NO confirmation)
 * - error: An error occurred during processing
 * 
 * Close Reasons (for enforcement of Success Closure Law):
 * - user-close: X button, ESC, backdrop click
 * - browser-back: Back button navigation
 * - success: After successful submit - ALWAYS bypasses confirmation
 * - fatal-error: Unrecoverable error - bypasses confirmation
 * - route-change: External navigation
 */
export type OverlayState = "idle" | "editing" | "processing" | "submitted" | "error";

export type CloseReason = 
  | "user-close"    // X, ESC, backdrop
  | "browser-back"  // Browser back button
  | "success"       // After successful submit - NEVER shows confirmation
  | "fatal-error"   // Unrecoverable error
  | "route-change"; // External navigation

export interface UseOverlayStateOptions {
  /** Optional initial state (defaults to "idle") */
  initialState?: OverlayState;
  /** External dirty flag - if provided, overrides internal tracking */
  externalIsDirty?: boolean;
}

export interface OverlayStateManager {
  /** Current state of the overlay */
  state: OverlayState;
  
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  
  /** Error message if state is "error" */
  errorMessage: string | null;
  
  /** Close reason for the current close attempt */
  closeReason: CloseReason | null;
  
  // State transitions
  
  /** Mark as dirty and transition to "editing" state */
  startEditing: () => void;
  
  /** Transition to "processing" state (blocks close) */
  startProcessing: () => void;
  
  /** Transition to "submitted" state (allows immediate close, sets reason to "success") */
  complete: () => void;
  
  /** Transition to "error" state with optional message */
  fail: (error?: string) => void;
  
  /** Reset to "idle" state */
  reset: () => void;
  
  /** Set dirty state explicitly (for external form tracking) */
  setDirty: (dirty: boolean) => void;
  
  /**
   * Request close with explicit reason - returns true if close should proceed immediately
   * 
   * Rules:
   * - success/fatal-error: ALWAYS bypass confirmation
   * - processing: ALWAYS block
   * - clean forms: close immediately
   * - dirty forms with user-close/browser-back: need confirmation (returns false)
   */
  requestClose: (reason: CloseReason) => boolean;
  
  /** Clear close reason (when close is cancelled by user) */
  clearCloseReason: () => void;
  
  /** 
   * Synchronous check if submitted (for blocker race condition fix)
   * This reads from a ref, not React state, so it's always current
   */
  isSubmittedSync: () => boolean;
  
  // Derived booleans for UI
  
  /** Whether the overlay can be closed immediately (no confirmation needed) */
  canClose: boolean;
  
  /** Whether close actions should be blocked (processing state) */
  isBlocked: boolean;
  
  /** Whether to show confirmation dialog (dirty and user-initiated close) */
  shouldConfirm: boolean;
}

export function useOverlayState(options: UseOverlayStateOptions = {}): OverlayStateManager {
  const { initialState = "idle", externalIsDirty } = options;
  
  const [state, setState] = useState<OverlayState>(initialState);
  const [internalIsDirty, setInternalIsDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState<CloseReason | null>(null);
  
  // SYNCHRONOUS ref for submitted state - survives React's async setState
  // This is the fix for the "confirmation dialog on success" race condition
  const isSubmittedRef = useRef(false);
  
  // Use external dirty flag if provided, otherwise use internal
  const isDirty = externalIsDirty !== undefined ? externalIsDirty : internalIsDirty;
  
  const startEditing = useCallback(() => {
    if (state === "idle") {
      setState("editing");
    }
    setInternalIsDirty(true);
  }, [state]);
  
  const startProcessing = useCallback(() => {
    setState("processing");
    setErrorMessage(null);
  }, []);
  
  // SUCCESS CLOSURE LAW: complete() sets state to "submitted" AND marks close reason as "success"
  // This ensures that any subsequent close bypasses confirmation
  // CRITICAL: Set ref FIRST (synchronous) before React state (async)
  const complete = useCallback(() => {
    isSubmittedRef.current = true; // SYNC - blocker reads this immediately
    setState("submitted");
    setInternalIsDirty(false);
    setCloseReason("success"); // Mark that next close is due to success
    setErrorMessage(null);
  }, []);
  
  const fail = useCallback((error?: string) => {
    setState("error");
    setErrorMessage(error || null);
  }, []);
  
  const reset = useCallback(() => {
    isSubmittedRef.current = false; // Reset sync ref
    setState("idle");
    setInternalIsDirty(false);
    setErrorMessage(null);
    setCloseReason(null);
  }, []);
  
  const setDirty = useCallback((dirty: boolean) => {
    setInternalIsDirty(dirty);
    if (dirty && state === "idle") {
      setState("editing");
    } else if (!dirty && state === "editing") {
      setState("idle");
    }
  }, [state]);
  
  /**
   * Request close with explicit reason
   * 
   * SUCCESS CLOSURE LAW ENFORCEMENT:
   * - success/fatal-error: ALWAYS returns true (bypass confirmation)
   * - processing: ALWAYS returns false (blocked)
   * - clean forms: returns true
   * - dirty forms: returns false (caller should show confirmation)
   */
  const requestClose = useCallback((reason: CloseReason): boolean => {
    setCloseReason(reason);
    
    // SUCCESS CLOSURE LAW: Success and fatal-error ALWAYS bypass confirmation
    // "An overlay that is closing due to success may never show a confirmation dialog. Ever."
    if (reason === "success" || reason === "fatal-error") {
      return true;
    }
    
    // Submitted state also bypasses (already succeeded)
    if (state === "submitted") {
      return true;
    }
    
    // Block during processing
    if (state === "processing") {
      return false;
    }
    
    // Clean forms close immediately
    if (!isDirty) {
      return true;
    }
    
    // Dirty forms need confirmation (handled by caller)
    return false;
  }, [state, isDirty]);
  
  const clearCloseReason = useCallback(() => {
    setCloseReason(null);
  }, []);
  
  // Synchronous getter for submitted state - survives React's async re-render
  const isSubmittedSync = useCallback(() => isSubmittedRef.current, []);
  
  // Derived values
  const derived = useMemo(() => {
    // Can close immediately if:
    // - idle (no changes)
    // - submitted (success, no confirmation EVER)
    // - error (failure is final, allow escape)
    // - not dirty
    // - closeReason is "success" or "fatal-error"
    const canClose = 
      state === "idle" || 
      state === "submitted" || 
      state === "error" || 
      !isDirty ||
      closeReason === "success" ||
      closeReason === "fatal-error";
    
    // Block all close attempts during processing
    const isBlocked = state === "processing";
    
    // Show confirmation ONLY if:
    // - dirty
    // - not in processing/submitted state
    // - close reason is user-initiated (not success/fatal-error)
    const shouldConfirm = 
      isDirty && 
      state !== "processing" && 
      state !== "submitted" &&
      closeReason !== "success" &&
      closeReason !== "fatal-error";
    
    return { canClose, isBlocked, shouldConfirm };
  }, [state, isDirty, closeReason]);
  
  return {
    state,
    isDirty,
    errorMessage,
    closeReason,
    startEditing,
    startProcessing,
    complete,
    fail,
    reset,
    setDirty,
    requestClose,
    clearCloseReason,
    isSubmittedSync,
    ...derived,
  };
}
